/**
 * Cursor-paging loop shared by every `/telemetry` reader: full-history
 * seed (call with `afterId: null`) and incremental follow-up (call
 * again with the previous result's `lastId` and `samples`) are the same
 * operation, since a seed is just a follow-up with nothing yet fetched.
 *
 * `maxSamples` bounds memory, not network calls: once exceeded, the
 * oldest samples (lowest id) are dropped so the loop keeps advancing
 * through every page up to the current tail instead of stopping partway
 * through a long session -- a caller that keeps polling past the cap
 * therefore sees a rolling window of the newest samples, never a frozen
 * one.
 */

import api from '../services/api';
import { SessionTelemetrySampleWithId } from '../types';

const DEFAULT_PAGE_SIZE = 5000;
const DEFAULT_MAX_SAMPLES = 50_000;
// Guards against a pathological cursor that never reaches next_after_id
// null (each iteration reads one page, so this bounds work per call
// without bounding how much of a session it can catch up on in one go).
const MAX_ITERATIONS = 200;

export interface TelemetryFetchResult {
  samples: SessionTelemetrySampleWithId[];
  lastId: number | null;
}

export async function fetchSessionTelemetryUpTo(
  sessionId: string,
  kind: 'gps' | 'imu',
  afterId: number | null,
  existing: SessionTelemetrySampleWithId[],
  options: { pageSize?: number; maxSamples?: number } = {},
): Promise<TelemetryFetchResult> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const maxSamples = options.maxSamples ?? DEFAULT_MAX_SAMPLES;

  let cursor = afterId ?? undefined;
  // Stays === existing, and `seen` stays unset, until a page actually
  // changes something -- so a no-op poll returns the same array
  // reference and a caller keyed on it (e.g. a React state setter) can
  // skip re-rendering.
  let samples = existing;
  let seen: Set<number> | undefined;

  const fork = () => {
    if (seen) return;
    samples = existing.slice();
    seen = new Set(samples.map((s) => s.id));
  };

  for (let i = 0; i < MAX_ITERATIONS; i += 1) {
    const page = await api.listSessionTelemetryPage(sessionId, {
      kind,
      afterId: cursor,
      limit: pageSize,
    });

    if (page.reload) {
      samples = [];
      seen = new Set();
    }

    if (page.samples.length > 0) {
      fork();
      for (const s of page.samples) {
        if (seen!.has(s.id)) continue;
        seen!.add(s.id);
        samples.push(s);
      }
      if (samples.length > maxSamples) {
        const dropped = samples.splice(0, samples.length - maxSamples);
        for (const d of dropped) seen!.delete(d.id);
      }
    }

    if (page.next_after_id == null) break;
    cursor = page.next_after_id;
  }

  return { samples, lastId: samples.length ? samples[samples.length - 1].id : null };
}
